import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import useHealth, { sanitizeWabaNumber } from '../../hooks/useHealth';
import { templateService } from '../../services/templateService';
import { radiusPx } from '../../../../theme/tokens';

export default function HealthLiveBar({
  currentBotNumber = '',
  companyId = '',
  onOpenFullHealth,
}) {
  const { phoneHealth, fetchPhoneHealth } = useHealth();
  const [activeNumber, setActiveNumber] = useState(currentBotNumber);

  useEffect(() => {
    let isMounted = true;
    async function resolveBot() {
      if (currentBotNumber) {
        setActiveNumber(currentBotNumber);
        fetchPhoneHealth(currentBotNumber, companyId);
        return;
      }
      if (companyId) {
        try {
          const res = await templateService.getBotNumbers(companyId);
          const rawList = Array.isArray(res.data) ? res.data : res.data?.list ?? res.data?.results ?? [];
          const firstBot = rawList[0]?.wa_phone_number;
          if (isMounted && firstBot) {
            setActiveNumber(firstBot);
            fetchPhoneHealth(firstBot, companyId);
          }
        } catch {
          // ignore
        }
      }
    }
    resolveBot();
    return () => {
      isMounted = false;
    };
  }, [currentBotNumber, companyId, fetchPhoneHealth]);

  if (!activeNumber && !phoneHealth.data) {
    return null;
  }

  const normQuality = String(phoneHealth.data?.qualityRating || 'UNKNOWN').toUpperCase();
  const normStatus = String(phoneHealth.data?.status || 'CONNECTED').toUpperCase();

  const isGreen = normQuality.includes('GREEN') || normQuality === 'HIGH';
  const isYellow = normQuality.includes('YELLOW') || normQuality === 'MEDIUM';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        p: 1.25,
        px: 2,
        mb: 1.5,
        borderRadius: radiusPx.md,
        bgcolor: '#f8fafc',
        border: '1px solid #e2e8f0',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            color: '#059669',
            fontWeight: 700,
            fontSize: '0.8125rem',
          }}
        >
          <MonitorHeartIcon sx={{ fontSize: 18 }} />
          <span>WABA Health:</span>
        </Box>

        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {phoneHealth.data?.displayPhoneNumber || activeNumber || '—'}
        </Typography>

        {phoneHealth.loading ? (
          <CircularProgress size={14} sx={{ color: '#059669' }} />
        ) : (
          <>
            <Chip
              size="small"
              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
              label={`Quality: ${normQuality}`}
              sx={{
                bgcolor: isGreen ? '#e8f5e9' : isYellow ? '#fff8e1' : '#f5f5f5',
                color: isGreen ? '#2e7d32' : isYellow ? '#ed6c02' : '#616161',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
              }}
            />
            <Chip
              size="small"
              label={normStatus}
              sx={{
                bgcolor: normStatus === 'CONNECTED' ? '#ecfdf5' : '#fef3c7',
                color: normStatus === 'CONNECTED' ? '#065f46' : '#92400e',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
              }}
            />
            {phoneHealth.data?.messagingLimitTier && phoneHealth.data.messagingLimitTier !== '—' && (
              <Chip
                size="small"
                label={`Limit: ${phoneHealth.data.messagingLimitTier}`}
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            )}
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Refresh health status" arrow>
          <span>
            <IconButton
              size="small"
              onClick={() => activeNumber && fetchPhoneHealth(activeNumber, companyId)}
              disabled={phoneHealth.loading || !activeNumber}
              sx={{ p: 0.5 }}
            >
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>

        <Button
          size="small"
          variant="text"
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          onClick={onOpenFullHealth}
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: '#059669',
            p: '2px 8px',
          }}
        >
          Open Health Studio
        </Button>
      </Box>
    </Box>
  );
}
